// 20 visually distinct resume layouts. Each layout consumes:
//   data:    ResumeData (contact, summary, experience, education, skills, languages, certifications, projects, achievements)
//   accent:  primary color (CSS string)
//   options: { photo_style, skill_style, sidebar_side, icons, ats_mode, columns, qr_url }
//
// Each component returns the inner content of .resume-sheet — the parent ResumeRenderer
// applies the sheet wrapper, theme CSS vars, and overall sizing.

import React from "react";
import Skills from "./Skills";

// ─────────── shared helpers ───────────
const fmtDates = (s, e) => (s && e ? `${s} – ${e}` : s || e || "");
const safe = (v, f="") => (v === undefined || v === null ? f : v);

const ContactInline = ({ c }) => (
  <p className="r-contact">
    {[c.location, c.phone, c.email, c.linkedin, c.website].filter(Boolean).join("  •  ")}
  </p>
);

const ContactStack = ({ c, icons }) => (
  <ul className="r-contact-stack">
    {c.location && <li>{icons && "📍 "}{c.location}</li>}
    {c.phone    && <li>{icons && "📞 "}{c.phone}</li>}
    {c.email    && <li>{icons && "✉ "}{c.email}</li>}
    {c.linkedin && <li>{icons && "in/ "}{c.linkedin}</li>}
    {c.website  && <li>{icons && "🌐 "}{c.website}</li>}
  </ul>
);

const Section = ({ title, accent, children, hidden }) => hidden ? null : (
  <section className="r-section">
    <h2 className="r-section-title" style={{ color: accent, borderColor: accent }}>{title}</h2>
    {children}
  </section>
);

const ExperienceList = ({ items, accent, dense }) => (
  <>{(items || []).map((x) => (
    <div key={x.id} className={dense ? "r-item r-item-dense" : "r-item"}>
      <div className="r-item-head">
        <div><strong>{safe(x.role)}</strong>{x.company && <span className="r-company"> · {x.company}</span>}</div>
        <div className="r-dates">{fmtDates(x.start_date, x.end_date)}{x.location ? ` · ${x.location}` : ""}</div>
      </div>
      {(x.bullets || []).filter(Boolean).length > 0 && (
        <ul className="r-bullets">{x.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
      )}
    </div>
  ))}</>
);

const EducationList = ({ items }) => (
  <>{(items || []).map((e) => (
    <div key={e.id} className="r-item">
      <div className="r-item-head">
        <div><strong>{safe(e.degree)}{e.field ? `, ${e.field}` : ""}</strong>{e.school && <span className="r-company"> · {e.school}</span>}</div>
        <div className="r-dates">{fmtDates(e.start_date, e.end_date)}{e.location ? ` · ${e.location}` : ""}</div>
      </div>
      {(e.gpa || e.notes) && <p className="r-notes">{[e.gpa && `GPA: ${e.gpa}`, e.notes].filter(Boolean).join(" — ")}</p>}
    </div>
  ))}</>
);

const Photo = ({ url, shape = "round", size = 96 }) => {
  if (!url || shape === "none") return null;
  const radius = shape === "round" ? "50%" : shape === "soft" ? "12px" : "0";
  return (
    <div className="r-photo" style={{
      width: size, height: size, borderRadius: radius,
      backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center",
      flexShrink: 0,
    }} />
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 20 DISTINCT LAYOUTS
// ═══════════════════════════════════════════════════════════════════════════

// 1. Classic — Name + contact centered, divider, then sections.
export const L_Classic = ({ data, accent, options }) => (
  <div className="L-classic">
    <header className="r-header-center">
      <h1 className="r-name">{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      <ContactInline c={data.contact} />
      <div className="r-divider" style={{ background: accent }} />
    </header>
    {data.summary && <Section title="Professional Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
    {data.achievements?.length > 0 && <Section title="Achievements" accent={accent}><ul className="r-bullets">{data.achievements.map((a, i) => <li key={i}>{a}</li>)}</ul></Section>}
  </div>
);

// 2. Banner Top — Full-width colored bar with white name, then sections below.
export const L_BannerTop = ({ data, accent, options }) => (
  <div className="L-banner-top">
    <header className="r-banner-header" style={{ background: accent }}>
      <h1 className="r-name" style={{ color: "#fff" }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title" style={{ color: "rgba(255,255,255,.85)" }}>{data.contact.title}</p>}
    </header>
    <p className="r-contact-bar">{[data.contact.location, data.contact.phone, data.contact.email, data.contact.linkedin].filter(Boolean).join("  •  ")}</p>
    {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education and Training" accent={accent}><EducationList items={data.education} /></Section>}
  </div>
);

// 3. Side Banner — Vertical colored strip on the left edge.
export const L_SideBanner = ({ data, accent, options }) => (
  <div className="L-side-banner">
    <div className="r-side-strip" style={{ background: accent }} />
    <div className="r-side-banner-body">
      <header>
        <h1 className="r-name" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
        {data.contact.title && <p className="r-title">{data.contact.title}</p>}
        <ContactInline c={data.contact} />
      </header>
      {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
      {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
      {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
      {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    </div>
  </div>
);

// 4. Sidebar Left — Dark left column (contact, skills, education) + light right column (experience).
export const L_SidebarLeft = ({ data, accent, options }) => (
  <div className="L-sidebar" style={{ "--side-bg": accent }}>
    <aside className="r-sidebar r-sidebar-left">
      {options.photo_style !== "none" && data.contact.photo && (
        <Photo url={data.contact.photo} shape={options.photo_style === "square" ? "soft" : "round"} size={120} />
      )}
      <h1 className="r-name-side" style={{ color: "#fff" }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title-side">{data.contact.title}</p>}
      <div className="r-side-block"><h3 className="r-side-title">Contact</h3><ContactStack c={data.contact} icons={options.icons} /></div>
      {data.skills?.length > 0 && (<div className="r-side-block"><h3 className="r-side-title">Skills</h3><Skills skills={data.skills} accent="#fff" style={options.skill_style} /></div>)}
      {data.languages?.length > 0 && (<div className="r-side-block"><h3 className="r-side-title">Languages</h3><ul className="r-side-list">{data.languages.map((l) => <li key={l.id}>{l.name} <em>· {l.proficiency}</em></li>)}</ul></div>)}
      {data.education?.length > 0 && (<div className="r-side-block"><h3 className="r-side-title">Education</h3>{data.education.map((e) => (<div key={e.id} className="r-side-edu"><strong>{e.degree}</strong><div>{e.school}</div><span>{fmtDates(e.start_date, e.end_date)}</span></div>))}</div>)}
    </aside>
    <main className="r-main">
      {data.summary && <Section title="Professional Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
      {data.experience?.length > 0 && <Section title="Work History" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
      {data.achievements?.length > 0 && <Section title="Key Achievements" accent={accent}><ul className="r-bullets">{data.achievements.map((a, i) => <li key={i}>{a}</li>)}</ul></Section>}
    </main>
  </div>
);

// 5. Sidebar Right — Mirror of L_SidebarLeft.
export const L_SidebarRight = (p) => (
  <div style={{ direction: "rtl" }} className="L-sidebar-right-wrap">
    <div style={{ direction: "ltr" }}>
      <L_SidebarLeft {...p} />
    </div>
  </div>
);

// 6. Two Column Equal — Two equal columns, name at top spanning both.
export const L_TwoColumn = ({ data, accent, options }) => (
  <div className="L-two-col">
    <header className="r-header-classic">
      <h1 className="r-name" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      <ContactInline c={data.contact} />
      <div className="r-divider" style={{ background: accent }} />
    </header>
    <div className="r-twocol-grid">
      <div>
        {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
        {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} dense /></Section>}
      </div>
      <div>
        {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
        {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
        {data.languages?.length > 0 && <Section title="Languages" accent={accent}><ul className="r-bullets">{data.languages.map((l) => <li key={l.id}>{l.name} · {l.proficiency}</li>)}</ul></Section>}
      </div>
    </div>
  </div>
);

// 7. Minimal — All centered, italic summary, lots of whitespace, no accent borders.
export const L_Minimal = ({ data, accent, options }) => (
  <div className="L-minimal">
    <header className="r-header-minimal">
      <h1 className="r-name">{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      <p className="r-contact-minimal">{[data.contact.location, data.contact.phone, data.contact.email].filter(Boolean).join("  ·  ")}</p>
    </header>
    {data.summary && <p className="r-summary-min">{data.summary}</p>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
  </div>
);

// 8. Elegant — All caps name, wide letter-spacing serif title, centered with thin double rule.
export const L_Elegant = ({ data, accent, options }) => (
  <div className="L-elegant">
    <header className="r-header-elegant" style={{ borderColor: accent }}>
      <h1 className="r-name-elegant">{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title-elegant">{data.contact.title}</p>}
      <ContactInline c={data.contact} />
    </header>
    {data.summary && <Section title="Profile" accent={accent}><p className="r-summary-italic">{data.summary}</p></Section>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
  </div>
);

// 9. ATS — Helvetica/Arial, no color, no fancy structures. Pure text bot-friendly.
export const L_ATS = ({ data, options }) => (
  <div className="L-ats">
    <h1 className="r-name-ats">{safe(data.contact.full_name, "Your Name")}</h1>
    <p className="r-contact-ats">{[data.contact.location, data.contact.phone, data.contact.email, data.contact.linkedin].filter(Boolean).join(" | ")}</p>
    {data.summary && (<><h2 className="r-section-title-ats">SUMMARY</h2><p>{data.summary}</p></>)}
    {data.experience?.length > 0 && (<><h2 className="r-section-title-ats">EXPERIENCE</h2><ExperienceList items={data.experience} accent="#000" /></>)}
    {data.education?.length > 0 && (<><h2 className="r-section-title-ats">EDUCATION</h2><EducationList items={data.education} /></>)}
    {data.skills?.length > 0 && (<><h2 className="r-section-title-ats">SKILLS</h2><p>{data.skills.map((s) => s.name).filter(Boolean).join(", ")}</p></>)}
    {data.certifications?.length > 0 && (<><h2 className="r-section-title-ats">CERTIFICATIONS</h2><ul>{data.certifications.map((c) => <li key={c.id}>{c.name} — {c.issuer} ({c.year})</li>)}</ul></>)}
  </div>
);

// 10. Creative — Colored top banner with name + title in white, contact stacked vertically right.
export const L_Creative = ({ data, accent, options }) => (
  <div className="L-creative">
    <header className="r-header-creative" style={{ background: accent }}>
      <div>
        <h1 className="r-name-creative">{safe(data.contact.full_name, "Your Name")}</h1>
        {data.contact.title && <p className="r-title-creative">{data.contact.title}</p>}
      </div>
      <ContactStack c={data.contact} icons />
    </header>
    {data.summary && <Section title="About" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
  </div>
);

// 11. Quotation — Large monolith block name on left, giant quote glyph on top right.
export const L_Quotation = ({ data, accent, options }) => (
  <div className="L-quotation">
    <header className="r-quote-header">
      <div className="r-quote-name">
        <h1 className="r-name-quote">{safe(data.contact.full_name, "Your Name")}</h1>
        {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      </div>
      <div className="r-quote-mark" style={{ color: accent }}>”</div>
    </header>
    <div className="r-twocol-grid">
      <div>
        <Section title="Contact" accent={accent}><ContactStack c={data.contact} icons={options.icons} /></Section>
        {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
        {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
      </div>
      <div>
        {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
        {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
      </div>
    </div>
  </div>
);

// 12. Greeting — "Hi, I'm Name." friendly heading in accent box.
export const L_Greeting = ({ data, accent, options }) => (
  <div className="L-greeting">
    <header className="r-greeting-header" style={{ background: accent }}>
      <h1 className="r-greeting-text">Hi, I&apos;m {safe(data.contact.full_name, "Your Name").split(" ")[0] || "Your Name"}.</h1>
      <p className="r-greeting-sub">{data.contact.title || "Experienced Professional"}</p>
    </header>
    <p className="r-contact-bar">{[data.contact.phone, data.contact.email, data.contact.location].filter(Boolean).join("  ·  ")}</p>
    {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
    {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education and Training" accent={accent}><EducationList items={data.education} /></Section>}
  </div>
);

// 13. Monogram — Two-letter initials in geometric diamond, name uppercase wide.
export const L_Monogram = ({ data, accent, options }) => {
  const name = safe(data.contact.full_name, "Your Name");
  const parts = name.split(/\s+/);
  const initials = (parts[0]?.[0] || "Y") + (parts[parts.length - 1]?.[0] || "N");
  return (
    <div className="L-monogram">
      <header className="r-mono-header">
        <div className="r-mono-badge" style={{ borderColor: accent, color: accent }}>
          <span>{initials.toUpperCase()}</span>
        </div>
        <div className="r-mono-name">
          <h1 className="r-name-uc">{name.toUpperCase()}</h1>
          <ContactInline c={data.contact} />
        </div>
      </header>
      {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
      {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
      {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
      {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    </div>
  );
};

// 14. Photo Top — Large circular photo + name to the right at top.
export const L_PhotoTop = ({ data, accent, options }) => (
  <div className="L-photo-top">
    <header className="r-photo-header">
      {data.contact.photo
        ? <Photo url={data.contact.photo} shape={options.photo_style === "square" ? "soft" : "round"} size={130} />
        : <div className="r-photo-placeholder" style={{ background: accent + "33", color: accent }}>{(data.contact.full_name || "Y")[0]}</div>}
      <div>
        <h1 className="r-name" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
        {data.contact.title && <p className="r-title">{data.contact.title}</p>}
        <ContactStack c={data.contact} icons={options.icons} />
      </div>
    </header>
    <div className="r-divider" style={{ background: accent }} />
    {data.summary && <Section title="Professional Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.experience?.length > 0 && <Section title="Work History" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
  </div>
);

// 15. Timeline — Vertical line on the left with dates and dot markers for each experience.
export const L_Timeline = ({ data, accent, options }) => (
  <div className="L-timeline">
    <header className="r-header-classic">
      <h1 className="r-name" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      <ContactInline c={data.contact} />
      <div className="r-divider" style={{ background: accent }} />
    </header>
    {data.summary && <Section title="Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.experience?.length > 0 && (
      <Section title="Career Timeline" accent={accent}>
        <div className="r-timeline-list">
          {data.experience.map((x) => (
            <div key={x.id} className="r-timeline-item">
              <div className="r-timeline-dates">{fmtDates(x.start_date, x.end_date)}</div>
              <div className="r-timeline-marker"><span style={{ background: accent }} /></div>
              <div className="r-timeline-body">
                <div><strong>{safe(x.role)}</strong>{x.company && <span className="r-company"> · {x.company}</span>}</div>
                <ul className="r-bullets">{(x.bullets || []).filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
            </div>
          ))}
        </div>
      </Section>
    )}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
  </div>
);

// 16. Cards — Each section wrapped in a soft card with shadow.
export const L_Cards = ({ data, accent, options }) => (
  <div className="L-cards">
    <header className="r-card-header" style={{ borderTop: `4px solid ${accent}` }}>
      <h1 className="r-name" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title">{data.contact.title}</p>}
      <ContactInline c={data.contact} />
    </header>
    {data.summary && <div className="r-card"><h2 className="r-section-title" style={{ color: accent }}>Summary</h2><p className="r-summary">{data.summary}</p></div>}
    {data.experience?.length > 0 && <div className="r-card"><h2 className="r-section-title" style={{ color: accent }}>Experience</h2><ExperienceList items={data.experience} accent={accent} /></div>}
    {data.skills?.length > 0 && <div className="r-card"><h2 className="r-section-title" style={{ color: accent }}>Skills</h2><Skills skills={data.skills} accent={accent} style={options.skill_style} /></div>}
    {data.education?.length > 0 && <div className="r-card"><h2 className="r-section-title" style={{ color: accent }}>Education</h2><EducationList items={data.education} /></div>}
  </div>
);

// 17. Newspaper — Two-column body with serif headline, decorative all-caps section heads.
export const L_Newspaper = ({ data, accent, options }) => (
  <div className="L-newspaper">
    <header className="r-newspaper-header" style={{ borderColor: accent }}>
      <h1 className="r-name-newspaper">{safe(data.contact.full_name, "Your Name")}</h1>
      <p className="r-title">{data.contact.title}</p>
      <ContactInline c={data.contact} />
    </header>
    {data.summary && <p className="r-news-lede">{data.summary}</p>}
    <div className="r-news-cols">
      {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} dense /></Section>}
      <div>
        {data.skills?.length > 0 && <Section title="Skills" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
        {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
      </div>
    </div>
  </div>
);

// 18. Magazine — Big bold name on left side, body text fills right column.
export const L_Magazine = ({ data, accent, options }) => (
  <div className="L-magazine">
    <div className="r-magazine-grid">
      <aside className="r-magazine-aside">
        <h1 className="r-name-magazine" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name").split(" ").join("\n")}</h1>
        {data.contact.title && <p className="r-title">{data.contact.title}</p>}
        <ContactStack c={data.contact} icons={options.icons} />
        {data.skills?.length > 0 && <><h3 className="r-section-title" style={{ color: accent }}>Skills</h3><Skills skills={data.skills} accent={accent} style={options.skill_style} /></>}
        {data.languages?.length > 0 && (<><h3 className="r-section-title" style={{ color: accent }}>Languages</h3><ul className="r-bullets">{data.languages.map((l) => <li key={l.id}>{l.name} · {l.proficiency}</li>)}</ul></>)}
      </aside>
      <main>
        {data.summary && <Section title="Profile" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
        {data.experience?.length > 0 && <Section title="Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
        {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
      </main>
    </div>
  </div>
);

// 19. Engineer — Tech-resume style with mono accent + projects emphasized.
export const L_Engineer = ({ data, accent, options }) => (
  <div className="L-engineer">
    <header className="r-engineer-header">
      <h1 className="r-name r-name-mono" style={{ color: accent }}>{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title r-mono">{data.contact.title}</p>}
      <p className="r-contact r-mono">{[data.contact.location, data.contact.phone, data.contact.email, data.contact.linkedin, data.contact.website].filter(Boolean).join(" | ")}</p>
    </header>
    {data.summary && <Section title="// Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.skills?.length > 0 && <Section title="// Tech Stack" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
    {data.experience?.length > 0 && <Section title="// Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.projects?.length > 0 && <Section title="// Projects" accent={accent}>{data.projects.map((p) => (<div key={p.id} className="r-item"><strong>{p.name}</strong> — {p.description}{p.url && <span> · {p.url}</span>}</div>))}</Section>}
    {data.education?.length > 0 && <Section title="// Education" accent={accent}><EducationList items={data.education} /></Section>}
  </div>
);

// 20. Executive — Bold senior-leader feel: tall left margin, large name, key achievements box on top.
export const L_Executive = ({ data, accent, options }) => (
  <div className="L-executive">
    <header className="r-exec-header" style={{ borderLeft: `6px solid ${accent}` }}>
      <h1 className="r-name r-name-exec">{safe(data.contact.full_name, "Your Name")}</h1>
      {data.contact.title && <p className="r-title r-title-exec" style={{ color: accent }}>{data.contact.title}</p>}
      <ContactInline c={data.contact} />
    </header>
    {data.achievements?.length > 0 && (
      <div className="r-exec-highlights" style={{ borderColor: accent }}>
        <h3 style={{ color: accent }}>Key Achievements</h3>
        <ul>{data.achievements.map((a, i) => <li key={i}>{a}</li>)}</ul>
      </div>
    )}
    {data.summary && <Section title="Executive Summary" accent={accent}><p className="r-summary">{data.summary}</p></Section>}
    {data.experience?.length > 0 && <Section title="Professional Experience" accent={accent}><ExperienceList items={data.experience} accent={accent} /></Section>}
    {data.education?.length > 0 && <Section title="Education" accent={accent}><EducationList items={data.education} /></Section>}
    {data.skills?.length > 0 && <Section title="Core Competencies" accent={accent}><Skills skills={data.skills} accent={accent} style={options.skill_style} /></Section>}
  </div>
);


// ─────────── Dispatch table ───────────
export const LAYOUTS = {
  classic:        L_Classic,
  banner_top:     L_BannerTop,
  side_banner:    L_SideBanner,
  sidebar_left:   L_SidebarLeft,
  sidebar_right:  L_SidebarRight,
  two_column:     L_TwoColumn,
  minimal:        L_Minimal,
  elegant:        L_Elegant,
  ats:            L_ATS,
  creative:       L_Creative,
  quotation:      L_Quotation,
  greeting:       L_Greeting,
  monogram:       L_Monogram,
  photo_top:      L_PhotoTop,
  timeline:       L_Timeline,
  cards:          L_Cards,
  newspaper:      L_Newspaper,
  magazine:       L_Magazine,
  engineer:       L_Engineer,
  executive:      L_Executive,
};

export const LAYOUT_LIST = [
  { slug: "classic",       name_ar: "كلاسيكي",       name_en: "Classic",         family: "professional" },
  { slug: "banner_top",    name_ar: "بانر علوي",     name_en: "Top Banner",      family: "modern" },
  { slug: "side_banner",   name_ar: "شريط جانبي",    name_en: "Side Banner",     family: "modern" },
  { slug: "sidebar_left",  name_ar: "شريط يساري",    name_en: "Sidebar Left",    family: "sidebar" },
  { slug: "sidebar_right", name_ar: "شريط يميني",    name_en: "Sidebar Right",   family: "sidebar" },
  { slug: "two_column",    name_ar: "عمودين",        name_en: "Two Columns",     family: "modern" },
  { slug: "minimal",       name_ar: "أنيق بسيط",     name_en: "Minimal",         family: "minimal" },
  { slug: "elegant",       name_ar: "راقي",          name_en: "Elegant",         family: "elegant" },
  { slug: "ats",           name_ar: "متوافق ATS",    name_en: "ATS",             family: "ats" },
  { slug: "creative",      name_ar: "إبداعي",        name_en: "Creative",        family: "creative" },
  { slug: "quotation",     name_ar: "اقتباس",        name_en: "Quotation",       family: "creative" },
  { slug: "greeting",      name_ar: "ترحيبي",        name_en: "Greeting",        family: "modern" },
  { slug: "monogram",      name_ar: "أحرف مونوغرام", name_en: "Monogram",        family: "elegant" },
  { slug: "photo_top",     name_ar: "صورة بأعلى",    name_en: "Photo Top",       family: "with-photo" },
  { slug: "timeline",      name_ar: "خط زمني",       name_en: "Timeline",        family: "modern" },
  { slug: "cards",         name_ar: "بطاقات",         name_en: "Cards",           family: "modern" },
  { slug: "newspaper",     name_ar: "صحيفة",          name_en: "Newspaper",       family: "creative" },
  { slug: "magazine",      name_ar: "مجلة",           name_en: "Magazine",        family: "creative" },
  { slug: "engineer",      name_ar: "مهندس / تقني",  name_en: "Engineer / Tech", family: "engineer" },
  { slug: "executive",     name_ar: "تنفيذي",         name_en: "Executive",       family: "executive" },
];
